import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Search, Plus } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface Bot {
  id: string;
  name: string;
  type: string;
  status: string;
}

interface BulkAddConditionalSkipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableBots: Bot[];
  onComplete: () => void;
}

export const BulkAddConditionalSkipDialog: React.FC<BulkAddConditionalSkipDialogProps> = ({
  open,
  onOpenChange,
  availableBots,
  onComplete
}) => {
  const [selectedBotIds, setSelectedBotIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [botTypeFilter, setBotTypeFilter] = useState<string>('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Bulk configuration
  const [skipType, setSkipType] = useState<'START_STOP_AFTER_LOSS' | 'FLEXIBLE_PATTERN'>('START_STOP_AFTER_LOSS');
  const [threshold, setThreshold] = useState(0);
  const [maxLossStreak, setMaxLossStreak] = useState<number | null>(null);
  const [maxLossBehavior, setMaxLossBehavior] = useState('reset_streak');
  const [forwardUrl, setForwardUrl] = useState('https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/bot-signal-merge-webhook');
  const [patterns, setPatterns] = useState<Array<{ lock_pattern: string; open_pattern: string; priority: number }>>([]);
  const [patternMode, setPatternMode] = useState<'NORMAL' | 'REVERSE'>('NORMAL');
  const [isPermanent, setIsPermanent] = useState(false);

  // Filter bots
  const filteredBots = availableBots.filter(bot => {
    const matchesType = botTypeFilter === 'all' || bot.type === botTypeFilter;
    const matchesSearch = searchQuery === '' || 
      bot.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bot.type.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const toggleBotSelection = (botId: string) => {
    setSelectedBotIds(prev => 
      prev.includes(botId) ? prev.filter(id => id !== botId) : [...prev, botId]
    );
  };

  const selectAll = () => {
    setSelectedBotIds(filteredBots.map(bot => bot.id));
  };

  const deselectAll = () => {
    setSelectedBotIds([]);
  };

  const handleBulkAdd = async () => {
    if (selectedBotIds.length === 0) {
      toast.error('Please select at least one bot');
      return;
    }

    if (skipType === 'FLEXIBLE_PATTERN' && patterns.length === 0) {
      toast.error('Please add at least one pattern rule for Type 2');
      return;
    }

    // Validate patterns
    if (skipType === 'FLEXIBLE_PATTERN') {
      for (const pattern of patterns) {
        if (!pattern.lock_pattern) {
          toast.error('Lock pattern cannot be empty');
          return;
        }
        if (!isPermanent && !pattern.open_pattern) {
          toast.error('Open pattern cannot be empty (unless Permanent Lock is enabled)');
          return;
        }
      }
    }

    setIsSubmitting(true);

    try {
      // Prepare settings data
      const settingsToInsert = selectedBotIds.map(botId => ({
        bot_id: botId,
        skip_type: skipType,
        enabled: true,
        loss_streak_threshold: skipType === 'START_STOP_AFTER_LOSS' ? threshold : 0,
        max_loss_streak: skipType === 'START_STOP_AFTER_LOSS' ? maxLossStreak : null,
        max_loss_behavior: skipType === 'START_STOP_AFTER_LOSS' ? maxLossBehavior : 'reset_streak',
        forward_url: forwardUrl
      }));

      // Insert settings
      const { data: insertedSettings, error: settingsError } = await supabase
        .from('conditional_skip_settings')
        .insert(settingsToInsert)
        .select();

      if (settingsError) throw settingsError;

      // If Type 2, insert patterns for each setting
      if (skipType === 'FLEXIBLE_PATTERN' && patterns.length > 0 && insertedSettings) {
        const patternInserts = insertedSettings.flatMap(setting =>
          patterns.map(p => ({
            setting_id: setting.id,
            lock_pattern: p.lock_pattern,
            open_pattern: isPermanent ? '' : p.open_pattern,
            priority: p.priority,
            enabled: true,
            pattern_mode: patternMode,
            is_permanent: isPermanent
          }))
        );

        const { error: patternsError } = await supabase
          .from('flexible_skip_patterns')
          .insert(patternInserts);

        if (patternsError) throw patternsError;

        // Create initial bot state for Type 2
        const initialBotStates = insertedSettings.map(setting => ({
          bot_id: setting.bot_id,
          mode: patternMode === 'REVERSE' ? 'GATE' : 'OPEN',
          current_loss_streak: 0,
          recent_results: '',
          locked_by_max_loss: false,
          manually_unlocked: false
        }));

        console.log('Creating initial bot states:', initialBotStates);

        // Delete existing states first
        const { error: deleteError } = await supabase
          .from('bot_current_condition_skip')
          .delete()
          .in('bot_id', insertedSettings.map(s => s.bot_id));

        if (deleteError) {
          console.error('Error deleting existing bot states:', deleteError);
          throw deleteError;
        }

        // Insert new states
        const { data: insertedStates, error: stateError } = await supabase
          .from('bot_current_condition_skip')
          .insert(initialBotStates)
          .select();

        if (stateError) {
          console.error('Error inserting bot states:', stateError);
          throw stateError;
        }

        console.log('Successfully created bot states:', insertedStates);

        // Verify all states were created
        if (!insertedStates || insertedStates.length !== initialBotStates.length) {
          throw new Error(`Failed to create all bot states. Expected ${initialBotStates.length}, got ${insertedStates?.length || 0}`);
        }
      }

      toast.success(`Successfully added ${selectedBotIds.length} conditional skip settings`);
      onComplete();
      onOpenChange(false);
      
      // Reset form
      setSelectedBotIds([]);
      setSearchQuery('');
      setBotTypeFilter('all');
      setSkipType('START_STOP_AFTER_LOSS');
      setThreshold(0);
      setMaxLossStreak(null);
      setMaxLossBehavior('reset_streak');
      setPatterns([]);
      setPatternMode('NORMAL');
      setIsPermanent(false);
      setShowPreview(false);
    } catch (error: any) {
      toast.error(`Failed to bulk add settings: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Add Conditional Skip Settings</DialogTitle>
          <DialogDescription>
            Select multiple bots and apply the same configuration to all of them
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Step 1: Select Bots */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Step 1: Select Bots</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search bots..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={botTypeFilter} onValueChange={setBotTypeFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="user_bot">User Bot</SelectItem>
                    <SelectItem value="prop_bot">Prop Bot</SelectItem>
                    <SelectItem value="shadow_bot">Shadow Bot</SelectItem>
                    <SelectItem value="premium_bot">Premium Bot</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAll}>
                  Select All ({filteredBots.length})
                </Button>
                <Button variant="outline" size="sm" onClick={deselectAll}>
                  Deselect All
                </Button>
                <Badge variant="secondary" className="ml-auto">
                  {selectedBotIds.length} selected
                </Badge>
              </div>

              <ScrollArea className="h-[200px] border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={filteredBots.length > 0 && selectedBotIds.length === filteredBots.length}
                          onCheckedChange={(checked) => checked ? selectAll() : deselectAll()}
                        />
                      </TableHead>
                      <TableHead>Bot Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBots.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                          No bots available
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredBots.map(bot => (
                        <TableRow key={bot.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedBotIds.includes(bot.id)}
                              onCheckedChange={() => toggleBotSelection(bot.id)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{bot.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{bot.type}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={bot.status === 'active' ? 'default' : 'secondary'}>
                              {bot.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Step 2: Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Step 2: Configure Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Skip Type</Label>
                <Select value={skipType} onValueChange={(v: any) => setSkipType(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="START_STOP_AFTER_LOSS">
                      Type 1: Start/Stop After Loss (Simple)
                    </SelectItem>
                    <SelectItem value="FLEXIBLE_PATTERN">
                      Type 2: Flexible Pattern Skip (Advanced)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {skipType === 'FLEXIBLE_PATTERN' && (
                <>
                  <div>
                    <Label>Pattern Mode</Label>
                    <Select value={patternMode} onValueChange={(v: any) => setPatternMode(v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NORMAL">
                          🔒 Normal: Lock closes gate, Open opens (default OPEN)
                        </SelectItem>
                        <SelectItem value="REVERSE">
                          🔄 Reverse: Open opens gate, Lock closes (default GATE)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {patternMode === 'REVERSE'
                        ? '🔄 Bot starts in GATE, opens when Open Pattern matches'
                        : '🔒 Bot starts in OPEN, closes when Lock Pattern matches'
                      }
                    </p>
                  </div>

                  <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
                    <Checkbox
                      id="permanent-lock"
                      checked={isPermanent}
                      onCheckedChange={(checked) => setIsPermanent(checked as boolean)}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label
                        htmlFor="permanent-lock"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        🔒 Permanent Lock
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Once locked, gate stays closed until admin manually unlocks it
                      </p>
                    </div>
                  </div>
                </>
              )}

              {skipType === 'START_STOP_AFTER_LOSS' ? (
                <>
                  <div>
                    <Label>Loss Streak Threshold</Label>
                    <Input
                      type="number"
                      min="0"
                      value={threshold}
                      onChange={(e) => setThreshold(parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Max Loss Streak (Optional)</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Leave empty to disable"
                      value={maxLossStreak || ''}
                      onChange={(e) => setMaxLossStreak(e.target.value ? parseInt(e.target.value) : null)}
                    />
                  </div>
                  <div>
                    <Label>Max Loss Behavior</Label>
                    <Select value={maxLossBehavior} onValueChange={setMaxLossBehavior}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reset_streak">Reset Streak</SelectItem>
                        <SelectItem value="lock_gate">Lock Gate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label>Pattern Rules</Label>
                  {patterns.map((pattern, idx) => (
                    <Card key={idx} className="p-3">
                      <div className="grid grid-cols-4 gap-2">
                        <Input
                          placeholder="Lock: 010"
                          value={pattern.lock_pattern}
                          onChange={(e) => {
                            const newPatterns = [...patterns];
                            newPatterns[idx].lock_pattern = e.target.value;
                            setPatterns(newPatterns);
                          }}
                          className="font-mono"
                        />
                        <Input
                          placeholder="Open: 11"
                          value={pattern.open_pattern}
                          onChange={(e) => {
                            const newPatterns = [...patterns];
                            newPatterns[idx].open_pattern = e.target.value;
                            setPatterns(newPatterns);
                          }}
                          className="font-mono"
                        />
                        <Input
                          type="number"
                          placeholder="Priority"
                          min="1"
                          value={pattern.priority}
                          onChange={(e) => {
                            const newPatterns = [...patterns];
                            newPatterns[idx].priority = parseInt(e.target.value) || 1;
                            setPatterns(newPatterns);
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPatterns(patterns.filter((_, i) => i !== idx))}
                        >
                          Remove
                        </Button>
                      </div>
                    </Card>
                  ))}
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setPatterns([...patterns, {
                      lock_pattern: '',
                      open_pattern: '',
                      priority: patterns.length + 1
                    }])}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Pattern Rule
                  </Button>
                </div>
              )}

              <div>
                <Label>Forward URL</Label>
                <Input
                  value={forwardUrl}
                  onChange={(e) => setForwardUrl(e.target.value)}
                  placeholder="https://example.com/webhook"
                />
              </div>
            </CardContent>
          </Card>

          {/* Step 3: Preview */}
          {selectedBotIds.length > 0 && (
            <Collapsible open={showPreview} onOpenChange={setShowPreview}>
              <Card>
                <CardHeader>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-0 hover:bg-transparent">
                      <CardTitle className="text-base">
                        Preview Changes ({selectedBotIds.length} bots)
                      </CardTitle>
                    </Button>
                  </CollapsibleTrigger>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent>
                    <ScrollArea className="h-[150px]">
                      <div className="space-y-2">
                        {selectedBotIds.map(botId => {
                          const bot = availableBots.find(b => b.id === botId);
                          return (
                            <div key={botId} className="p-2 bg-muted rounded text-sm">
                              <span className="font-medium">{bot?.name}</span>
                              <span className="text-muted-foreground ml-2">
                                → {skipType === 'START_STOP_AFTER_LOSS' ? 'Type 1' : 'Type 2'}
                                {skipType === 'START_STOP_AFTER_LOSS' && `, Threshold: ${threshold}`}
                                {skipType === 'FLEXIBLE_PATTERN' && (
                                  <>
                                    , {patterns.length} pattern(s)
                                    {patternMode === 'REVERSE' && ' 🔄 REVERSE'}
                                    {isPermanent && ' 🔒 PERMANENT'}
                                  </>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkAdd}
            disabled={isSubmitting || selectedBotIds.length === 0}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Add {selectedBotIds.length} Setting{selectedBotIds.length !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
